import { isLastDayOfMonth, isFirstDayOfMonth } from 'date-fns/fp';
import {
  Account,
  AverageMoneyIndicator,
  CashFlow,
  CashFlowCategoryIndicator,
  CashFlowTypeIndicator,
  ComplexPaymentIndicator,
  CreditInsights,
  DangerCashFlowIndicatorValues,
  ExplicitIndicatorValues,
  HeavyFailures,
  Overdraft,
  paymentIndicatorTypes,
  PaymentMethodIndicator,
  Transaction,
  TransactionBankOperationCategory,
  TransactionCategory,
  TransactionCreditCategory,
  TransactionExpenseCategory,
  TransactionRefundCategory,
  TransactionType,
  WarningIndicator,
  TransactionIncomeCategory,
  TransactionTaxCategory,
} from '@algoan/dashboard/model';
import {
  getTotalNumber,
  getTotalAmount,
  filterOutUndefined,
  parseDailyBalanceDate,
  selectDate,
  sumDailyBalancesFromAccounts,
  filterCheckingAndSavingsAccounts,
  didEventOccur,
} from '@algoan/dashboard/utils';

export const computeDifferedPaymentIndicator = (cashFlows: CashFlow[]): boolean =>
  cashFlows.some((cashFlow) => cashFlow.category === TransactionExpenseCategory.DEFERRED_TOTAL_PAYMENT);

export const computeDangerCashFlowIndicatorValues = (
  cashFlows: CashFlow[],
): DangerCashFlowIndicatorValues | undefined => {
  const calculateIndicator = getIndicatorCalculator(cashFlows);

  const dangerCashFlowIndicatorValues = {
    bankIncidentFees: calculateIndicator([TransactionBankOperationCategory.FEES_INCIDENT]),
    checkRejection: calculateIndicator([TransactionBankOperationCategory.REJECTION_CHECK]),
    checkRejectionFees: calculateIndicator([TransactionBankOperationCategory.FEES_CHECK_REJECTION]),
    gambling: calculateIndicator([TransactionExpenseCategory.GAMBLING]),
    interventionFees: calculateIndicator([TransactionBankOperationCategory.FEES_INTERVENTION]),
    locDrawdown: calculateIndicator([
      TransactionCreditCategory.LOAN_DRAWDOWN,
      TransactionCreditCategory.LOAN_DRAWDOWN_PERSONAL,
      TransactionCreditCategory.LOAN_DRAWDOWN_REAL_ESTATE,
      TransactionCreditCategory.LOAN_DRAWDOWN_REVOLVING,
      TransactionCreditCategory.LOAN_DRAWDOWN_FAST,
      TransactionCreditCategory.LOAN_DRAWDOWN_STUDENT,
    ]),
    overdraftFees: calculateIndicator([TransactionBankOperationCategory.FEES_OVERDRAFT]),
    paymentRejection: calculateIndicator([TransactionBankOperationCategory.REJECTION_PAYMENT]),
    paymentRejectionFees: calculateIndicator([TransactionBankOperationCategory.FEES_PAYMENT_REJECTION]),
    loanPaymentRejection: calculateIndicator([TransactionBankOperationCategory.REJECTION_LOAN_REPAYMENT]),
    debtCollection: calculateIndicator([TransactionCreditCategory.DEBT_COLLECTION]),
    provisionDirectDebtRecovery: calculateIndicator([TransactionBankOperationCategory.PROVISION_DIRECT_DEBT_RECOVERY]),
  };

  const hasAtLeastOneDangerCashFlowValue = Object.values(dangerCashFlowIndicatorValues).some(
    (indicator) => indicator !== undefined,
  );

  return hasAtLeastOneDangerCashFlowValue ? dangerCashFlowIndicatorValues : undefined;
};

export const computeExplicitIndicatorValues = (
  creditInsights: CreditInsights,
  historicalDepth: number,
  transactions: Transaction[],
  accounts: Account[],
  analysisDate: string,
): ExplicitIndicatorValues | undefined => {
  const cashFlows = creditInsights.cashFlows;

  const calculateIndicatorNumberOfDaysUsingTransactionData = getIndicatorCalculatorByNumberOfDaysUsingTransactionData(
    transactions,
    analysisDate,
  );
  const calculateIndicatorByUsingTransactionData = getIndicatorCalculatorByTypeUsingTransactionData(transactions);

  const numberOfDaysInAMonth = 31;

  // these are indicators unique to the cofidis debt consolidation synthesis widget.
  const explicitIndicatorValues: ExplicitIndicatorValues = {
    averageMoneyIndicator: calculateAverageMoneyIndicator(cashFlows, historicalDepth),
    checkUsage: calculateCheckUsageIndicator(cashFlows),
    cardType: calculateCardTypeIndicator(cashFlows),
    presenceOfANegativeBalance: computeNegativeBalanceIndicator(accounts),
    overdraftFees: getExplicitIndicator(cashFlows, TransactionBankOperationCategory.FEES_OVERDRAFT),
    interventionFees: getExplicitIndicator(cashFlows, TransactionBankOperationCategory.FEES_INTERVENTION),
    bankIncidentFees: getExplicitIndicator(cashFlows, TransactionBankOperationCategory.FEES_INCIDENT),
    internalTransfer: calculateIndicatorByUsingTransactionData([TransactionType.INCOMING_INTERNAL_TRANSFER]),
    paymentRejection: calculatePaymentRejectionIndicatorValue(numberOfDaysInAMonth, historicalDepth, cashFlows),
    checkRejection: calculateCheckRejectionIndicatorValue(numberOfDaysInAMonth, historicalDepth, cashFlows),
    telecomExpenditure: calculateTelecomsExpenditureIndicatorValue(numberOfDaysInAMonth, historicalDepth, cashFlows),
    locDrawdownsOfThePast30Days: calculateIndicatorNumberOfDaysUsingTransactionData(
      [
        TransactionCreditCategory.LOAN_DRAWDOWN,
        TransactionCreditCategory.LOAN_DRAWDOWN_PERSONAL,
        TransactionCreditCategory.LOAN_DRAWDOWN_REAL_ESTATE,
        TransactionCreditCategory.LOAN_DRAWDOWN_REVOLVING,
      ],
      30,
    ),
  };

  const hasAtLeastOneExplicitIndicatorValue = Object.values(explicitIndicatorValues).some(
    (indicator) => indicator !== undefined,
  );

  return hasAtLeastOneExplicitIndicatorValue ? explicitIndicatorValues : undefined;
};

export const calculateIfThirdPartyHolderNoticeEventOccurred = (
  heavyFailures: HeavyFailures,
): boolean | WarningIndicator => {
  const event = didEventOccur(heavyFailures, 'directRecoveryOfDebt');
  if (event) {
    return {
      type: 'directRecoveryOfDebt',
      nbTransactions: heavyFailures.directRecoveryOfDebt.transactions.length,
      references: heavyFailures.directRecoveryOfDebt.transactions.map((transaction) => transaction.id),
    };
  } else {
    return false;
  }
};

export const getInternationalTransfers = (transactions: Transaction[]): Transaction[] => {
  return transactions.filter((transaction) => transaction.enrichments?.isInternationalTransfer);
};

export const hasAtLeastOneInternationalTransfer = (transactions: Transaction[]): boolean => {
  return transactions.some((transaction) => transaction.enrichments?.isInternationalTransfer);
};

export const calculateInternationalTransfersIndicator = (
  transactions: Transaction[] | undefined,
): undefined | WarningIndicator => {
  if (!transactions) {
    return undefined;
  }
  const internationalTransfers = getInternationalTransfers(transactions);
  if (hasAtLeastOneInternationalTransfer(transactions)) {
    return {
      type: 'internationalTransfers',
      nbTransactions: internationalTransfers.length,
      references: internationalTransfers.map((transaction) => transaction.id),
    };
  }
  return undefined;
};

export const calculateIfBankAccountSeizureEventOccurred = (
  heavyFailures: HeavyFailures,
): boolean | WarningIndicator => {
  const event = didEventOccur(heavyFailures, 'bankAccountSeizure');
  if (event) {
    return {
      type: 'bankAccountSeizure',
      nbTransactions: heavyFailures.bankAccountSeizure.transactions.length,
      references: heavyFailures.bankAccountSeizure.transactions.map((transaction) => transaction.id),
    };
  } else {
    return false;
  }
};

export const calculateOverdraftIndicatorValue = (
  overdraft: Overdraft,
): { days: number; maxAmount: number } | undefined => {
  if (overdraft.duration === 0 || overdraft.minimumBalanceReached === 0) return undefined;
  return { days: overdraft.duration, maxAmount: Math.abs(overdraft.minimumBalanceReached) };
};

const calculateAverageMoneyIndicator = (cashFlows: CashFlow[], historicalDepth: number): AverageMoneyIndicator => {
  const numberOfDaysInAMonth = 31;
  const atmCashFlows = cashFlows.filter(
    (cashFlow) =>
      cashFlow.category === TransactionExpenseCategory.ATM || cashFlow.type === TransactionType.CASH_WITHDRAWAL,
  );

  return {
    averageNumber: Math.ceil((numberOfDaysInAMonth * getTotalNumber(atmCashFlows)) / historicalDepth),
    averageMoney: Math.abs((numberOfDaysInAMonth * getTotalAmount(atmCashFlows)) / historicalDepth),
  };
};

const calculateCheckUsageIndicator = (cashFlows: CashFlow[]): CashFlowCategoryIndicator => {
  const filteredCashFlows = cashFlows.filter(
    (cashFlow) => cashFlow.type === TransactionType.CHECK || cashFlow.category === TransactionExpenseCategory.CHECK,
  );

  const { count, amount } = filteredCashFlows.reduce(
    (indicator, cashFlow) => {
      return {
        count: indicator.count + cashFlow.transactions.length,
        amount: indicator.amount + cashFlow.totalAmount,
      };
    },
    { count: 0, amount: 0 },
  );
  return {
    category: [TransactionExpenseCategory.CHECK],
    amount,
    count,
  };
};

const calculateCardTypeIndicator = (cashFlows: CashFlow[]): 'DIFFERED' | 'DIRECT' | 'NONE' => {
  const hasCard = cashFlows.some(
    (cashFlow) =>
      ([TransactionExpenseCategory.CARD_PAYMENT, TransactionRefundCategory.REFUND] as TransactionCategory[]).includes(
        cashFlow.category,
      ) || cashFlow.type === TransactionType.CARD,
  );

  const hasDifferedPaymentCard = cashFlows.some(
    (cashFlow) => cashFlow.category === TransactionExpenseCategory.DEFERRED_TOTAL_PAYMENT,
  );

  return hasDifferedPaymentCard ? 'DIFFERED' : hasCard ? 'DIRECT' : 'NONE';
};

const calculatePaymentRejectionIndicatorValue = (
  numberOfDaysInAMonth: number,
  historicalDepth: number,
  cashFlows: CashFlow[],
): number => {
  const calculateIndicator = getIndicatorCalculator(cashFlows);

  return Math.ceil(
    (numberOfDaysInAMonth *
      (calculateIndicator([
        TransactionBankOperationCategory.REJECTION_PAYMENT,
        TransactionBankOperationCategory.REJECTION_LOAN_REPAYMENT,
        TransactionBankOperationCategory.FEES_PAYMENT_REJECTION,
      ])?.count ?? 0)) /
      historicalDepth,
  );
};

const calculateCheckRejectionIndicatorValue = (
  numberOfDaysInAMonth: number,
  historicalDepth: number,
  cashFlows: CashFlow[],
): number => {
  const calculateIndicator = getIndicatorCalculator(cashFlows);

  return Math.ceil(
    (numberOfDaysInAMonth *
      (calculateIndicator([
        TransactionBankOperationCategory.REJECTION_CHECK,
        TransactionBankOperationCategory.FEES_CHECK_REJECTION,
      ])?.count ?? 0)) /
      historicalDepth,
  );
};

const calculateTelecomsExpenditureIndicatorValue = (
  numberOfDaysInAMonth: number,
  historicalDepth: number,
  cashFlows: CashFlow[],
): number => {
  const calculateIndicator = getIndicatorCalculator(cashFlows);

  return Math.ceil(
    (numberOfDaysInAMonth * (calculateIndicator([TransactionExpenseCategory.TELECOM])?.amount ?? 0)) / historicalDepth,
  );
};

const getIndicatorCalculator =
  (cashFlows: CashFlow[]) =>
  <T extends TransactionCategory>(
    transactionCategory: T[],
  ): { type: T[]; category: T[]; count: number; amount: number } | undefined => {
    const transactionCategoriesTyped = transactionCategory as TransactionCategory[];
    const filteredCashFlows = cashFlows.filter((cashFlow) => transactionCategoriesTyped.includes(cashFlow.category));

    if (filteredCashFlows.length === 0) return undefined;

    const { count, amount } = filteredCashFlows.reduce(
      (indicator, cashFlow) => {
        return {
          count: indicator.count + cashFlow.transactions.length,
          amount: indicator.amount + cashFlow.totalAmount,
        };
      },
      { count: 0, amount: 0 },
    );

    return {
      type: transactionCategory,
      category: transactionCategory,
      count,
      amount: Math.abs(amount),
    };
  };

const getIndicatorCalculatorByTypeUsingTransactionData =
  (transactions: Transaction[]) =>
  <T extends TransactionType>(cashFlowType: T[]): CashFlowTypeIndicator | undefined => {
    const cashFlowTypesTyped = cashFlowType as TransactionType[];
    const filteredTransactions = transactions.filter(
      (transaction) => transaction.enrichments?.type && cashFlowTypesTyped.includes(transaction.enrichments.type),
    );

    const { transactionCount, transactionAmount } = filteredTransactions.reduce(
      (indicator, transaction) => {
        return {
          transactionCount: indicator.transactionCount + 1,
          transactionAmount: indicator.transactionAmount + transaction.amount,
        };
      },
      { transactionCount: 0, transactionAmount: 0 },
    );

    return {
      type: cashFlowType,
      count: transactionCount,
      amount: Math.abs(transactionAmount),
    };
  };

const getIndicatorCalculatorByNumberOfDaysUsingTransactionData =
  (transactions: Transaction[], analysisDate: string) =>
  <T extends TransactionCategory>(
    transactionCategory: T[],
    numberOfDaysOfHistory: number,
  ): CashFlowCategoryIndicator | undefined => {
    const milisecondsOfHistory = numberOfDaysOfHistory * 24 * 60 * 60 * 1000;
    const transactionCategoryTyped = transactionCategory as TransactionCategory[];
    const filteredTransactions = transactions.filter((transaction) =>
      transactionCategoryTyped.includes(transaction.enrichments?.category as TransactionCategory),
    );

    const { transactionCount, transactionAmount } = filteredTransactions.reduce(
      (indicator, transaction) => {
        if (Date.parse(selectDate(transaction.dates)) < Date.parse(analysisDate) - milisecondsOfHistory) {
          return indicator;
        }
        return {
          transactionCount: indicator.transactionCount + 1,
          transactionAmount: indicator.transactionAmount + transaction.amount,
        };
      },
      { transactionCount: 0, transactionAmount: 0 },
    );

    return {
      category: transactionCategory,
      count: transactionCount,
      amount: Math.abs(transactionAmount),
    };
  };

const getExplicitIndicator = (cashFlows: CashFlow[], cashFlowType: TransactionCategory): CashFlowCategoryIndicator => {
  const cashFlowIndicator = getIndicatorCalculator(cashFlows)([cashFlowType]);

  return {
    category: [cashFlowType],
    amount: cashFlowIndicator?.amount ?? 0,
    count: cashFlowIndicator?.count ?? 0,
  };
};

const computeNegativeBalanceIndicator = (everyAccounts: Account[]): boolean => {
  const checkingsAccounts = filterCheckingAndSavingsAccounts(everyAccounts);
  const dailyBalances = sumDailyBalancesFromAccounts(checkingsAccounts);

  return dailyBalances
    .filter((dailyBalance) => {
      const dailyBalanceDate = parseDailyBalanceDate(dailyBalance.date);

      return isFirstDayOfMonth(dailyBalanceDate) || isLastDayOfMonth(dailyBalanceDate);
    })
    .some((dailyBalance) => dailyBalance.balance < 0);
};

export const computePaymentMethods = (transactions: Transaction[]): PaymentMethodIndicator[] => {
  const paymentMethods = filterOutUndefined(getComplexPaymentIndicators(transactions));

  return paymentMethods;
};

const getComplexPaymentIndicators = (transactions: Transaction[]): ComplexPaymentIndicator[] => {
  const groupedTransactionsByType = transactions.reduce<Record<string, Transaction[]>>((groups, transaction) => {
    if (transaction.enrichments?.type === undefined) {
      return groups;
    }

    if (!paymentIndicatorTypes.includes(transaction.enrichments.type)) {
      return groups;
    }

    return {
      ...groups,
      [transaction.enrichments.type]: [...(groups[transaction.enrichments.type] || []), transaction],
    };
  }, {});

  return Object.keys(groupedTransactionsByType).map((transactionType) => {
    const totalAmount = groupedTransactionsByType[transactionType].reduce(
      (accumulator, transaction) => transaction.amount + accumulator,
      0,
    );
    const nbOfTransactions = groupedTransactionsByType[transactionType].length;

    return {
      amount: Math.abs(totalAmount),
      count: nbOfTransactions,
      type: transactionType as TransactionType,
    };
  });
};

const ignoreCategoriesForJustification: TransactionCategory[] = [
  TransactionIncomeCategory.WAGE,
  TransactionIncomeCategory.WAGE_ADVANCE_PAYMENT,
  TransactionIncomeCategory.WAGE_BONUS,
  TransactionIncomeCategory.WAGE_SALARY_ADVANCE,
  TransactionIncomeCategory.ALLOWANCE,
  TransactionIncomeCategory.ALLOWANCE_FAMILY,
  TransactionIncomeCategory.ALLOWANCE_HEALTH,
  TransactionIncomeCategory.ALLOWANCE_INCOME_SUPPORT,
  TransactionIncomeCategory.ALLOWANCE_UNEMPLOYMENT,
  TransactionIncomeCategory.STATE_SUBSIDY,
  TransactionIncomeCategory.RETIREMENT_PENSION,
  TransactionIncomeCategory.REVERSIONARY_PENSION,
  TransactionRefundCategory.REFUND_INSURANCE,
  TransactionTaxCategory.PERSO_TAX_REFUND,
  TransactionTaxCategory.PRO_TAX_REFUND,
  TransactionTaxCategory.PERSO_TAX_CREDIT,
  TransactionBankOperationCategory.REJECTION_CHECK,
  TransactionBankOperationCategory.REJECTION_LOAN_REPAYMENT,
  TransactionBankOperationCategory.REJECTION_PAYMENT,
  TransactionRefundCategory.REFUND,
  TransactionRefundCategory.REFUND_EXPENSE_REPORT,
  TransactionRefundCategory.REFUND_INSURANCE,
];

const alertAmount = 2000;

const getTransactionsToBeJustified = (transactions: Transaction[]): Transaction[] => {
  return transactions.filter(
    (transaction) =>
      transaction.enrichments?.category &&
      !ignoreCategoriesForJustification.includes(transaction.enrichments?.category) &&
      Math.abs(transaction.amount) >= alertAmount,
  );
};

const getInternalTransferTransactionsWithoutRelatedTransactions = (transactions: Transaction[]): Transaction[] => {
  return transactions.filter(
    (transaction) =>
      transaction.enrichments?.type === TransactionType.OUTGOING_INTERNAL_TRANSFER &&
      (!transaction.enrichments?.relatedTransactions ||
        Object.keys(transaction.enrichments?.relatedTransactions).length === 0),
  );
};

export const calculateBankTransactionsToBeJustifiedIndicatorValue = (
  transactions: Transaction[] | undefined,
): undefined | WarningIndicator => {
  if (!transactions) {
    return undefined;
  }

  const bankTransactionsToBeJustified = getTransactionsToBeJustified(transactions);

  if (bankTransactionsToBeJustified.length === 0) {
    return undefined;
  }

  return {
    type: 'bankTransactionsToBeJustified',
    nbTransactions: bankTransactionsToBeJustified.length,
    references: bankTransactionsToBeJustified.map((transaction) => transaction.id),
  };
};

export const calculateInternalTransfersWithoutRelatedTransactions = (
  transactions: Transaction[] | undefined,
): undefined | WarningIndicator => {
  if (!transactions) {
    return undefined;
  }

  const internalTransferTransactionsWithoutRelatedTransactions =
    getInternalTransferTransactionsWithoutRelatedTransactions(transactions);

  if (internalTransferTransactionsWithoutRelatedTransactions.length === 0) {
    return undefined;
  }

  return {
    type: 'internalTransfersWithoutRelatedTransactions',
    nbTransactions: internalTransferTransactionsWithoutRelatedTransactions.length,
    references: internalTransferTransactionsWithoutRelatedTransactions.map((transaction) => transaction.id),
  };
};
